import React, { useEffect, useState } from 'react';
import { User, UserPreferences } from '../types';

type UserProfile = {
    id?: string;
    name?: string;
    email?: string;
    verified?: number | boolean;
    totalReviews?: number;
    helpfulVotes?: number;
    interests?: any;
    preferences?: any;
    joinedAt?: string;
    type?: string;
};

type ProfileSettingsProps = {
    user: User;
    onEdit?: (preferences: UserPreferences) => void;
    onInterestsEdit?: () => void;
    onPreferencesEdit?: () => void;
};

const formatDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user, onEdit, onInterestsEdit, onPreferencesEdit }) => {
    if (!user) {
        return (
            <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                <p className="text-gray-600">No user data loaded.</p>
            </div>
        );
    }

    const [interests, setInterests] = useState<any[]>(
        () => (Array.isArray(user?.interests) ? user.interests : [])
    );
    const [prefs, setPrefs] = useState(() => user.preferences ?? {});
    const [activeTab, setActiveTab] = useState<'profile' | 'settings'>('profile');
    const [notificationSettings, setNotificationSettings] = useState({
        newReviews: prefs.notificationSettings?.newReviews ?? false,
        businessUpdates: prefs.notificationSettings?.businessUpdates ?? false,
        socialShares: prefs.notificationSettings?.socialShares ?? false,
        weeklyDigest: prefs.notificationSettings?.weeklyDigest ?? false,
    });

    useEffect(() => {
        setInterests(Array.isArray(user?.interests) ? user.interests : []);
        setNotificationSettings({
            newReviews: prefs.notificationSettings?.newReviews ?? false,
            businessUpdates: prefs.notificationSettings?.businessUpdates ?? false,
            socialShares: prefs.notificationSettings?.socialShares ?? false,
            weeklyDigest: prefs.notificationSettings?.weeklyDigest ?? false,
        });
        setPrefs(user.preferences ?? {});
    }, [user, prefs.notificationSettings, user.preferences]);

    const renderProfileTab = () => (<><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-800">
        <div>
            <p className="text-gray-500">Name</p>
            <p className="font-medium">{user.name || '—'}</p>
        </div>
        <div>
            <p className="text-gray-500">Email</p>
            <p className="font-medium">{user.email || '—'}</p>
        </div>
        {/* <div>
                            <p className="text-gray-500">User ID</p>
                            <p className="font-medium break-all">{user.id || '—'}</p>
                        </div> */}
        <div>
            <p className="text-gray-500">Verified</p>
            <p className="font-medium">{user.verified ? 'Yes' : 'No'}</p>
        </div>
        {/* <div>
                            <p className="text-gray-500">Type</p>
                            <p className="font-medium">{user.type || '—'}</p>
                        </div> */}
        <div>
            <p className="text-gray-500">Joined</p>
            <p className="font-medium">{formatDate(user.joinedAt.toString())}</p>
        </div>
        <div>
            <p className="text-gray-500">Total reviews</p>
            <p className="font-medium">{user.totalReviews ?? 0}</p>
        </div>
        <div>
            <p className="text-gray-500">Helpful votes</p>
            <p className="font-medium">{user.helpfulVotes ?? 0}</p>
        </div>

    </div></>);

    const renderSettingsTab = () => (<> <div className="space-y-4">
        {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-800">
                            <div>
                                <p className="text-gray-500">Total reviews</p>
                                <p className="font-medium">{user.totalReviews ?? 0}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Helpful votes</p>
                                <p className="font-medium">{user.helpfulVotes ?? 0}</p>
                            </div>
                            <div>
                                <p className="text-gray-500">Joined</p>
                                <p className="font-medium">{formatDate(user.joinedAt)}</p>
                            </div>
                        </div> */}
        <div className="space-y-6 min-w-0">
            <div className="space-y-3 min-w-0 rounded-lg bg-gray-50 p-3">
                <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-500">Interests</p>
                    <button onClick={onInterestsEdit} className="flex-shrink-0 text-sm text-primary-600 hover:text-primary-700 font-medium">Edit Interests</button>
                </div>
                {interests.length ? (
                    <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto rounded-lg border border-gray-100 bg-white p-2 sm:grid-cols-3 lg:grid-cols-4">
                        {interests.map((i: any, idx: number) => (
                            <span
                                key={i.id || idx}
                                className="inline-flex max-w-full items-center rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 ring-1 ring-primary-200"
                            >
                                <span className="truncate">{i.name || i.id || 'Interest'}</span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-600">None</p>
                )}
            </div>

            <div className="space-y-3 pb-6">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">Preferences</p>
                    {/* <button onClick={onPreferencesEdit} className="text-sm text-primary-600 hover:text-primary-700">Edit</button> */}
                </div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-700">Max distance</span>
                        <span className="text-sm font-semibold text-gray-900">{prefs.maxDistance ?? 'Any'}</span>
                    </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-700">Minimum rating</span>
                        <span className="text-sm font-semibold text-gray-900">{prefs.minimumRating ?? 'Any'}</span>
                    </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-700">Show verified only</span>
                        <span className="text-sm font-semibold text-gray-900">
                            {prefs.showVerifiedOnly ? 'Yes' : 'No'}
                        </span>
                    </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-700">Price range</span>
                        <span className="text-sm font-semibold text-gray-900">
                            {Array.isArray(prefs.priceRange) && prefs.priceRange.length
                                ? prefs.priceRange.join(', ')
                                : 'Any'}
                        </span>
                    </div>
                </div>
                <div className="space-y-3">
                    <div className="flex items-center justify-between pt-3">
                        <p className="text-sm text-gray-500">Notifications</p>
                    </div>
                    <div className="flex justify-between items-center text-sm font-medium text-gray-900">
                        <span>New reviews</span>
                        <div className='flex gap-3 justify-center items-center'>
                            <p>{notificationSettings.newReviews ? 'On' : 'Off'}</p>
                            <label className="relative inline-flex items-center cursor-pointer">

                                <input
                                    type="checkbox"
                                    checked={notificationSettings.newReviews}
                                    onChange={(e) => {
                                        const updated = { ...notificationSettings, newReviews: e.target.checked };
                                        setNotificationSettings(updated);
                                        onEdit?.({
                                            ...prefs,
                                            notificationSettings: updated
                                        } as UserPreferences);
                                    }}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                        </div>
                    </div>
                    <div className="flex justify-between items-center text-sm font-medium text-gray-900">
                        <span>Business updates</span>
                        <div className='flex gap-3 justify-center items-center'>
                            <p>{notificationSettings.businessUpdates ? 'On' : 'Off'}</p>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={notificationSettings.businessUpdates}
                                    onChange={(e) => {
                                        const updated = { ...notificationSettings, businessUpdates: e.target.checked };
                                        setNotificationSettings(updated);
                                        onEdit?.({
                                            ...prefs,
                                            notificationSettings: updated
                                        } as UserPreferences);
                                    }}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label></div>
                    </div>
                    <div className="flex justify-between items-center text-sm font-medium text-gray-900">
                        <span>Social shares</span>
                        <div className='flex gap-3 justify-center items-center'>
                            <p>{notificationSettings.socialShares ? 'On' : 'Off'}</p>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={notificationSettings.socialShares}
                                    onChange={(e) => {
                                        const updated = { ...notificationSettings, socialShares: e.target.checked };
                                        setNotificationSettings(updated);
                                        onEdit?.({
                                            ...prefs,
                                            notificationSettings: updated
                                        } as UserPreferences);
                                    }}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                        </div>
                    </div>
                    <div className="flex justify-between items-center text-sm font-medium text-gray-900">
                        <span>Weekly digest</span>
                        <div className='flex gap-3 justify-center items-center'>
                            <p>{notificationSettings.weeklyDigest ? 'On' : 'Off'}</p>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={notificationSettings.weeklyDigest}
                                    onChange={(e) => {
                                        const updated = { ...notificationSettings, weeklyDigest: e.target.checked };
                                        setNotificationSettings(updated);
                                        onEdit?.({
                                            ...prefs,
                                            notificationSettings: updated
                                        } as UserPreferences);
                                    }}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    </>);

    return (
        <div className="w-full bg-white mx-auto overflow-hidden md:flex">
            {/* Left column: avatar + info + menu */}
            <div className="bg-white md:border-r border-gray-200 p-4 space-y-4 md:w-64">

                <div className="flex items-center space-x-3">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-xl font-semibold">
                        {user.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-900">{user.name || '—'}</p>
                        <p className="text-xs text-gray-600">{user.email || '—'}</p>
                    </div>
                </div>
              
                    <div className="flex md:flex-col md:space-y-2 border-b md:border-0">
                    
                          <button
                            onClick={() => setActiveTab('profile')}
                            className={`w-full text-center md:text-left px-3 py-2 rounded-lg ${activeTab === 'profile' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50'}`}
                        >
                            My Profile
                        </button>
                        <button
                            onClick={() => setActiveTab('settings')}
                            className={`w-full text-center md:text-left px-3 py-2 rounded-lg ${activeTab === 'settings' ? 'bg-primary-50 text-primary-700 font-medium' : 'hover:bg-gray-50'}`}
                        >
                            Settings
                        </button>
                    </div>
              

            </div>

            {/* Right column: settings/detail area */}
            <div className="bg-white p-4 space-y-4 w-full flex-1 min-w-0 overflow-y-auto">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {activeTab === 'profile' ? 'Profile' : 'Settings'}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {activeTab === 'profile' ? 'Basic account details' : 'Account settings and preferences'}
                        </p>
                    </div>
                    {activeTab === 'settings' && (
                        <button
                            type="button"
                            onClick={onPreferencesEdit}
                            className="btn-primary px-3 py-1 text-sm"
                        >
                            Edit
                        </button>)}
                </div>

                {/* Main Content */}
                <div>
                    {activeTab === 'profile' && renderProfileTab()}
                    {activeTab === 'settings' && renderSettingsTab()}
                </div>

            </div>



        </div>
    );
};

export default ProfileSettings;